import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AcqStatus } from '@/types.ts'
import { SetFilterValue } from '@/stores/shared.ts'
import { DefaultFiltersState } from '@/stores'
import { useAppSelector } from '@/stores/hooks.ts'

export interface B2CRegistryTableState extends DefaultFiltersState {
  dateFrom?: string
  dateTo?: string
  b2cState?: AcqStatus | 'ALL'
}

const initialState: B2CRegistryTableState = {
  dateFrom: undefined,
  dateTo: undefined,
  b2cState: undefined,
  page: 0,
  showCount: 10,
}

const b2cRegistrySlice = createSlice({
  name: 'b2cRegistry',
  initialState,
  selectors: {
    filters: (state) => ({ page: state.page, showCount: state.showCount, sort: state.sort }),
    resettableStateFilters: (state, filters: (keyof B2CRegistryTableState)[]) => filters.map((e) => state[e]),
  },
  reducers: {
    setFilter: (state, action: PayloadAction<SetFilterValue<B2CRegistryTableState>>) => {
      return {
        ...state,
        page: action.payload.filterKey !== 'page' ? 0 : state.page,
        [action.payload.filterKey]: action.payload.filterValue,
      }
    },
    resetFilters: (state, action: PayloadAction<(keyof B2CRegistryTableState)[]>) => {
      const patch = Object.fromEntries(action.payload.map((key) => [key, initialState[key]]))
      return { ...state, ...patch }
    },
    resetAllFilters: (state) => {
      return { ...state, ...initialState }
    },
  },
})

export const {
  reducer: b2cRegistryReducer,
  selectors: b2cRegistrySelectors,
  actions: b2cRegistryActions,
} = b2cRegistrySlice

export const useB2CRegistryState = (resettableKeyFilters: (keyof B2CRegistryTableState)[] = []) => {
  const b2cState = useAppSelector((state) => state.b2cRegistry.b2cState)
  const { page, sort, showCount } = useAppSelector(b2cRegistrySelectors.filters)
  const dateRange = useAppSelector((state) => [state.b2cRegistry.dateFrom, state.b2cRegistry.dateTo])
  const resettableStateFilters = useAppSelector((state) =>
    b2cRegistrySelectors.resettableStateFilters(state, resettableKeyFilters)
  )

  return { b2cState, dateRange, resettableStateFilters, page, sort, showCount }
}
